import {
  type ConsoleEventData,
  type ErrorEventData,
  type NavigationEventData,
  type NetworkEventData,
  type ReplayBatchEnvelope,
  type ReplayEvent,
  type SessionEndEventData,
  type SessionStartEventData,
  type SnapshotEventData,
  type ViewportEventData,
} from "./schema";
import { record, addCustomEvent } from "rrweb";
import type { eventWithTime } from "rrweb";
import { createBatchSender, type IdentifyPayload } from "./transport";
import { createSessionRuntime } from "./runtime";
import {
  createConsoleCapture,
  createErrorCapture,
  createNavigationCapture,
  createNetworkCapture,
  createPerformanceCapture,
  createRageDeadClickCapture,
  createViewportCapture,
  errorDataFromValue,
} from "./signals";
import { fetchRemoteConfig } from "./remote-config";
import { getBrowserFingerprint, setStoredFingerprint } from "./fingerprint";
import { redactUrlForStorage, dashboardHostFromApiHost } from "./utils";
import type { ReplayController, WebReplayConfig } from "./types";

interface CaptureSlot {
  stop: () => void;
}

export function initReplay(config: WebReplayConfig): ReplayController {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("initReplay must run in a browser context");
  }

  // Honor Do-Not-Track when the customer opts in: install NOTHING and return an
  // inert controller, so no data is captured or sent.
  if (config.respectDoNotTrack) {
    const dnt =
      navigator.doNotTrack === "1" ||
      (window as unknown as { doNotTrack?: string }).doNotTrack === "1" ||
      (navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack === "1";
    if (dnt) {
      const noop = () => {};
      return {
        sessionId: "",
        flush: async () => {},
        identify: noop,
        track: noop,
        captureException: noop,
        setAnonymousId: noop,
        setMetadata: noop,
        stop: async () => {},
        urlForCurrentSession: () => null,
      };
    }
  }

  const session = createSessionRuntime(config);
  const sendBatch = createBatchSender(config);
  const { fp: fingerprint } = getBrowserFingerprint();
  sendBatch.setFingerprint(fingerprint);
  // Bind identity to THIS session: re-attach a persisted identify() only when the
  // same session resumed (reload); a fresh/rolled-over session starts anonymous.
  // Must run BEFORE the config/runtime identify() below so they replace/merge
  // over the correct base rather than a stale prior-session identity.
  sendBatch.bindSession(session.sessionId, session.resumed);
  const captures: Record<string, CaptureSlot | null> = {
    console: null,
    network: null,
    errors: null,
    navigation: null,
    viewport: null,
    rrweb: null,
  };
  let sampled = true;
  // The workspace has spent its plan's session allowance (remote shouldNotRecord).
  // A HARD gate, separate from `sampled`, because the always-record overrides
  // below flip `sampled` back on for errors/identified users — and a maxed plan
  // must not record even those. Nothing flips this back within a page load.
  let blocked = false;

  // Visibility gate + snapshot-ordering flags. Following the reference tracker:
  // defer the ENTIRE session start until the tab is first VISIBLE (never record a
  // hidden / prerendered tab), and only let a batch ship once the session's DOM
  // snapshot exists — so a batch never creates a server-side session before its
  // full_snapshot is in it.
  //  - `started`      : the visibility-gated start has run (tab was/became visible).
  //  - `snapshotReady`: rrweb's first full_snapshot has been buffered.
  //  - `rrwebFailed`  : record() threw, so no snapshot will ever arrive.
  // flush()/finalFlush() ship nothing until `started && (snapshotReady ||
  // rrwebFailed)`. Net effect: a tab that loads hidden and is closed while still
  // hidden creates NO session, and a started session's first batch always carries
  // the snapshot (or rrweb is known-failed and the no-replay session ships for the
  // backend to keep-if-analytics / reap-if-empty).
  let started = false;
  let snapshotReady = false;
  let rrwebFailed = false;
  // ── Inactivity end + session rotation ──────────────────────────────────────
  // `idle` = the session was ENDED after sessionInactivityMs with no MEANINGFUL
  // activity (interaction / navigation / track — not background network/perf/
  // heartbeat). While idle, emit() drops everything so a page that keeps polling
  // its API can't extend a session nobody is in. The next meaningful activity
  // rotates to a fresh session. `lastMeaningfulAt` is only bumped by markMeaningful().
  let idle = false;
  let lastMeaningfulAt = Date.now();
  let idleTimer: number | undefined;

  if (config.distinctId)
    sendBatch.setIdentify({ distinctId: config.distinctId });


  const emit = <TData>(
    event: Omit<ReplayEvent<TData>, "id" | "offsetMs" | "source">,
  ) => {
    // Session was ended for inactivity and nobody's back yet — drop everything
    // (background network/perf/console/heartbeat) so it can't extend a dead
    // session. resumeFromIdle() clears `idle` before re-emitting into the fresh one.
    if (idle) return;
    session.push({
      id: session.makeEventId(),
      // Clamp to >= 0. Some events timestamp themselves off performance
      // timeOrigin (paint timing, web vitals via PerformanceObserver), which
      // can predate session.startedAt when the SDK initialises after the page
      // has already loaded — yielding a negative offset. The server's
      // offset_ms column is an unsigned int, so a single negative offset
      // rejects the whole batch insert and silently drops those events.
      offsetMs: Math.max(0, event.ts - session.startedAt),
      source: session.sdk.platform,
      ...event,
    });
  };

  // Console sink. One capture, two destinations:
  //   1. our `type: "console"` event → the queryable session_events
  //      store the AI reads (full serialized args live here).
  //   2. a compact marker teed into the rrweb stream via addCustomEvent
  //      so the player can render console frame-aligned with the DOM.
  // We ship only level+message into rrweb (not the heavy args) to avoid
  // duplicating the full payload, and only while rrweb is recording —
  // addCustomEvent throws if no recording is active.
  const emitConsole = (d: ConsoleEventData) => {
    emit({ ts: Date.now(), type: "console", data: d });
    if (captures.rrweb) {
      try {
        addCustomEvent("console", { level: d.level, message: d.message });
      } catch {
        /* rrweb not recording / plugin unavailable — skip the tee */
      }
    }
  };

  const emitSessionStart = () => {
    const data: SessionStartEventData = {
      // Scrub credentials (jwt/token/password/reset-password/…) out of the entry
      // URL + referrer before they're stored — magic-link / OAuth / reset flows
      // routinely carry them in the query string. Fires before remote config, so
      // only the always-on default key scrub applies here (the critical case).
      href: redactUrlForStorage(window.location.href),
      path: window.location.pathname,
      referrer: redactUrlForStorage(document.referrer),
      title: document.title || undefined,
    };
    emit({ ts: Date.now(), type: "session_start", data });
  };

  // Last time rrweb emitted ANYTHING (full or incremental). Drives the
  // idle-aware heartbeat: on a busy page rrweb is already streaming, so the
  // forced 30s full snapshot is pure overhead — we only force one once the page
  // has gone quiet (where the stream would otherwise end and a mid-join viewer
  // would have no base).
  let lastRrwebActivityAt = Date.now();
  const emitSnapshot = (rrwebEvent: eventWithTime) => {
    lastRrwebActivityAt = Date.now();
    // The session becomes shippable the moment its first FULL snapshot (type 2)
    // is buffered — every later incremental is a diff against it, so a batch sent
    // before this would create an unplayable "no replay" session server-side.
    if (rrwebEvent.type === 2) snapshotReady = true;
    const data: SnapshotEventData = { recorder: "rrweb", rrwebEvent };
    emit({
      ts: rrwebEvent.timestamp,
      type: rrwebEvent.type === 2 ? "full_snapshot" : "incremental_snapshot",
      data,
    });
  };

  const emitViewport = () => {
    const data: ViewportEventData = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    emit({ ts: Date.now(), type: "viewport", data });
  };

  const emitSessionEnd = (reason: SessionEndEventData["reason"]) => {
    emit({ ts: Date.now(), type: "session_end", data: { reason } });
  };

  const flush = async () => {
    if (!sampled || blocked) {
      session.drain();
      return;
    }
    // Visibility + snapshot gate: hold (keep buffering, don't ship) until the
    // gated start has run AND rrweb's snapshot is buffered (or rrweb is known to
    // have failed). This is what guarantees the first batch that creates the
    // server-side session carries the full_snapshot — never a snapshot-less "no
    // replay" row — and that a not-yet-visible tab ships nothing at all.
    if (!started || (!snapshotReady && !rrwebFailed)) return;
    // minDuration gate: hold (keep buffering, don't ship) until the session has
    // lived past minDurationMs, so bounce sessions shorter than the minimum are
    // never uploaded (they're discarded on unload). 0 disables. Bounded by the
    // runtime buffer cap.
    if (minDurationMs > 0 && Date.now() - session.startedAt < minDurationMs) {
      return;
    }
    const events = session.drain();
    if (events.length === 0) return;
    const envelope: ReplayBatchEnvelope = {
      projectId: config.projectId,
      sessionId: session.sessionId,
      segmentId: session.segmentId,
      sequence: session.nextSequence(),
      sentAt: Date.now(),
      sdk: session.sdk,
      page: session.pageContext(),
      events,
    };
    const nextEnvelope = config.beforeSend
      ? (config.beforeSend({ envelope })?.envelope ?? null)
      : envelope;
    if (!nextEnvelope) return;
    try {
      await sendBatch(nextEnvelope);
    } catch {
      // Send ultimately failed (network down, ingest outage, server error). Put
      // the events back so the NEXT flush retries them rather than dropping the
      // batch — dropping the first batch loses the rrweb FullSnapshot and makes
      // the whole recording unplayable ("no replay frames"). Bounded in
      // requeue(), so a sustained outage can't grow the buffer without limit.
      session.requeue(events);
    }
  };

  const scheduleFlush = () => {
    void flush();
  };

  // End the current session because there's been no MEANINGFUL activity for
  // sessionInactivityMs. Ship its closing batch (with session_end), then gate
  // emit() so the page's background polling can't keep the dead session alive.
  const endForIdle = () => {
    if (idle || !started) return;
    emitSessionEnd("inactivity");
    void flush(); // drains synchronously into the send before we gate below
    idle = true;
  };

  // A user came back after an inactivity end → the return is a NEW session, not
  // a continuation. Rotate identity, re-open with session_start + a fresh rrweb
  // FullSnapshot (its base), and resume. Same anon/identified user (transport
  // identify/fingerprint state is untouched).
  const resumeFromIdle = () => {
    if (!idle) return;
    idle = false;
    session.rotate();
    snapshotReady = false; // the new session must ship its own full_snapshot first
    emitSessionStart();
    if (captures.rrweb) {
      try {
        record.takeFullSnapshot(true);
      } catch {
        /* recorder inactive — the session ships as no-replay (rrwebFailed gate) */
      }
    }
    emitViewport();
    lastMeaningfulAt = Date.now();
    scheduleFlush();
  };

  // The ONLY signal that resets the inactivity clock: genuine engagement —
  // interactions, real navigations, and explicit track/identify/exception calls.
  // Deliberately NOT background network/perf/console/heartbeat or app-driven DOM
  // re-renders, so a page that merely keeps polling its API ages out. Also
  // revives a session that was already ended.
  const markMeaningful = () => {
    lastMeaningfulAt = Date.now();
    session.touch();
    if (idle) resumeFromIdle();
  };

  const syncCapture = (
    key: string,
    want: boolean,
    install: () => (() => void) | undefined,
  ) => {
    const slot = captures[key];
    if (want && !slot) {
      const stop = install();
      captures[key] = stop ? { stop } : null;
      return;
    }
    if (!want && slot) {
      try {
        slot.stop();
      } catch {
        /* ignore */
      }
      captures[key] = null;
    }
  };

  // Track the privacy options the rrweb recorder was installed with so we
  // can detect a divergence when remote config arrives and tear-down +
  // re-install with the new settings. Without this, a user who flips
  // `maskAllInputs` in Settings would still see plaintext inputs in the
  // current session — the recorder was already running.
  let installedPrivacy: {
    maskAllInputs?: boolean;
    maskTextSelector?: string;
    blockSelector?: string;
    recordCanvas?: boolean;
    recordCrossOriginIframes?: boolean;
    maskInputOptionsKey?: string;
  } | null = null;

  // URL redaction policy applied to outgoing network events. The network
  // capture reads this when serialising each event so query-strings can
  // be stripped / allowlisted, and credit-card-looking text scrubbed,
  // before they leave the browser.
  let urlRedactionPolicy: {
    strip: boolean;
    allowed: string[];
    blockCC: boolean;
  } = { strip: false, allowed: [], blockCC: false };
  // Read live per-request by createNetworkCapture via the getUrlPolicy closure,
  // so a remote-config privacy update applies without re-installing the capture.

  // Sampling overrides — populated when /v1/sdk/config arrives. Referenced
  // inside error + identify handlers so they can flip a dropped session
  // back into recording.
  let sampleOverrides: {
    alwaysRecordErrors: boolean;
    alwaysRecordIdentified: boolean;
  } | null = null;
  let minDurationMs = 0;

  const installCaptures = (effective: {
    console: boolean;
    network: boolean;
    errors: boolean;
    /** Web vitals + long tasks + memory. Defaults to ON locally,
     *  but the workspace can flip it off via Settings → Recording. */
    performance?: boolean;
    captureHeaders: boolean;
    captureBodies?: boolean;
    maskAllInputs?: boolean;
    /** rrweb maskInputOptions — e.g. { password: true, email: true } */
    maskInputOptions?: Record<string, boolean>;
    maskTextSelector?: string;
    blockSelector?: string;
    recordCanvas?: boolean;
    recordCrossOriginIframes?: boolean;
    blockCreditCardText?: boolean;
    stripQueryParams?: boolean;
    allowedQueryParams?: string[];
    redactUrls?: Array<string | RegExp>;
    /** Phase-1 callers set this — start console/network/errors but defer
     *  rrweb until phase 2 (after remote config or 300ms timeout) so we
     *  don't lose the FullSnapshot to a stop+restart. */
    skipRrweb?: boolean;
  }) => {
    if (effective.skipRrweb) {
      // Bypass rrweb install; install the non-rrweb captures only.
      syncCapture("console", effective.console, () =>
        createConsoleCapture(emitConsole),
      );
      syncCapture("network", effective.network, () =>
        createNetworkCapture(
          {
            ...config,
            captureHeaders: effective.captureHeaders,
            redactUrls: effective.redactUrls ?? config.redactUrls,
          },
          (d: NetworkEventData) =>
            emit({ ts: d.startedAt, type: "network", data: d }),
          // Live URL-redaction policy — read per request so the remote
          // strip/allowlist/blockCC applies without re-installing the capture.
          () => urlRedactionPolicy,
        ),
      );
      syncCapture("errors", effective.errors, () =>
        createErrorCapture((d: ErrorEventData) => {
          if (!blocked && !sampled && sampleOverrides?.alwaysRecordErrors) sampled = true;
          emit({ ts: Date.now(), type: "error", data: d });
        }),
      );
      syncCapture("navigation", true, () =>
        createNavigationCapture((d: NavigationEventData) => {
          markMeaningful(); // a real route change revives an idle session
          emit({ ts: Date.now(), type: "navigation", data: d });
        }),
      );
      syncCapture("viewport", true, () =>
        createViewportCapture(() => {
          emitViewport();
          scheduleFlush();
        }),
      );
      syncCapture("rageDead", true, () =>
        createRageDeadClickCapture((d) =>
          emit({ ts: Date.now(), type: "custom", data: d }),
        ),
      );
      // Performance signals (LCP/CLS/FID/longtask/memory) — ride along
      // even in the rrweb-skipped phase-1 branch so we don't miss the
      // initial paint window. Each metric becomes a `kind: "perf"`
      // custom event; the backend extracts them later.
      syncCapture("performance", effective.performance !== false, () =>
        createPerformanceCapture(
          (d) => emit({ ts: d.ts ?? Date.now(), type: "performance", data: d }),
          {
            apiHost: config.apiHost,
            captureResourceTimings: config.captureResourceTimings,
            resourceMinDurationMs: config.resourceMinDurationMs,
          },
        ),
      );
      return;
    }
    const nextPrivacy = {
      maskAllInputs: effective.maskAllInputs ?? config.maskAllInputs ?? true, // safe default
      maskTextSelector: effective.maskTextSelector ?? config.maskTextSelector,
      blockSelector: effective.blockSelector ?? config.blockSelector,
      // Host config can force these on without waiting for remote config.
      recordCanvas: effective.recordCanvas ?? config.recordCanvas ?? false,
      recordCrossOriginIframes:
        effective.recordCrossOriginIframes ??
        config.recordCrossOriginIframes ??
        false,
      // Serialise input-type masks so the change-detection below can use a
      // simple string equality check.
      maskInputOptionsKey: Object.keys(
        effective.maskInputOptions ?? { password: true },
      )
        .sort()
        .join(","),
    };
    const privacyChanged =
      installedPrivacy !== null &&
      (installedPrivacy.maskAllInputs !== nextPrivacy.maskAllInputs ||
        installedPrivacy.maskTextSelector !== nextPrivacy.maskTextSelector ||
        installedPrivacy.blockSelector !== nextPrivacy.blockSelector ||
        installedPrivacy.recordCanvas !== nextPrivacy.recordCanvas ||
        installedPrivacy.recordCrossOriginIframes !==
          nextPrivacy.recordCrossOriginIframes ||
        installedPrivacy.maskInputOptionsKey !==
          nextPrivacy.maskInputOptionsKey);
    if (privacyChanged && captures.rrweb) {
      // Stop the old recorder; new snapshot will fire on re-record so the
      // server gets a fresh full snapshot with the new privacy applied.
      captures.rrweb.stop();
      captures.rrweb = null;
    }
    if (!captures.rrweb) {
      // Only pass options to rrweb when we have a value. Some option names
      // are version-sensitive (rrweb 2.0-alpha tightened canvas/iframe
      // option validation) — passing `false` explicitly can throw on init.
      // Letting them default to rrweb's own defaults is safer.
      const rrwebOpts: Record<string, unknown> = { emit: emitSnapshot };
      // Re-emit a full snapshot periodically. rrweb only snapshots once on
      // record(); if THAT batch is lost (network blip / ingest outage), the
      // recording is unplayable — incremental mutations with no base to apply
      // them to. A periodic checkout self-heals a lost initial snapshot and lets
      // a live viewer join mid-session. Paired with the flush re-queue.
      rrwebOpts.checkoutEveryNms = 120000;
      // Default privacy floor for VISIBLE TEXT: mask email addresses in every
      // text node (opt out with maskTextEmails:false), and optionally long digit
      // runs (maskTextNumbers). maskAllInputs already covers input VALUES; this
      // covers free-text PII the reference tracker masks by default.
      const maskEmails = config.maskTextEmails !== false;
      if (maskEmails || config.maskTextNumbers) {
        rrwebOpts.maskTextFn = (text: string): string => {
          let t = text;
          if (maskEmails)
            t = t.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]");
          if (config.maskTextNumbers)
            t = t.replace(/\b\d{5,}\b/g, (m) =>
              "•".repeat(Math.min(m.length, 12)),
            );
          return t;
        };
      }
      if (typeof nextPrivacy.maskAllInputs === "boolean")
        rrwebOpts.maskAllInputs = nextPrivacy.maskAllInputs;
      if (nextPrivacy.maskTextSelector)
        rrwebOpts.maskTextSelector = nextPrivacy.maskTextSelector;
      if (nextPrivacy.blockSelector)
        rrwebOpts.blockSelector = nextPrivacy.blockSelector;
      if (nextPrivacy.recordCanvas) rrwebOpts.recordCanvas = true;
      if (nextPrivacy.recordCrossOriginIframes)
        rrwebOpts.recordCrossOriginIframes = true;
      // Always-on input-type masks (password by default, plus anything the
      // workspace's mask rules implied via `input[type="X"]` selectors).
      // This wins even when maskAllInputs is false.
      if (
        effective.maskInputOptions &&
        Object.keys(effective.maskInputOptions).length > 0
      ) {
        rrwebOpts.maskInputOptions = effective.maskInputOptions;
      } else {
        rrwebOpts.maskInputOptions = { password: true };
      }
      let stop: (() => void) | undefined;
      let heartbeat: ReturnType<typeof setInterval> | undefined;
      try {
        stop = record(rrwebOpts as Parameters<typeof record>[0]);
        // rrweb only snapshots on ACTIVITY, so an idle page stops emitting and
        // the replay stream ends at the last DOM mutation — far short of the real
        // wall-clock session — while a lost/absent base leaves the whole recording
        // unplayable ("no replay frames"). Force a full snapshot on a wall-clock
        // TIMER (every 30s, regardless of activity): this keeps the stream
        // spanning the whole session (idle included) and guarantees a FRESH BASE
        // exists within 30s of any point — self-healing a lost initial snapshot,
        // surviving the privacy-driven restart below (which doesn't re-snapshot),
        // and enabling live mid-join. The SDK analog of the reference tracker's
        // periodic keepalive.
        heartbeat = setInterval(() => {
          // Never force a snapshot while the tab is HIDDEN. A backgrounded tab has
          // no visible activity to record, so forcing keepalive snapshots there
          // just pads the session with a dead idle tail (the frameless-long-session
          // phantom). The reference tracker likewise lets hidden time drive a
          // restart, not blind timers. It resumes forcing once the tab is visible.
          if (document.visibilityState === "hidden") return;
          // Idle-aware: skip the forced full snapshot when rrweb already emitted
          // within the last interval (the page is active and self-heals via its
          // own stream + the 2-min checkout). Only force one when the page has
          // gone quiet — keeping the self-heal for idle pages without
          // re-serializing a busy large-DOM app every 30s.
          if (Date.now() - lastRrwebActivityAt < 30_000) return;
          try {
            record.takeFullSnapshot(true);
          } catch {
            /* recorder not active — ignore */
          }
        }, 30_000);
        // Priority-flush the initial FullSnapshot now instead of waiting for the
        // 3s auto-flush, so the recording is playable ASAP and the most important
        // batch (the base) is the first one persisted + retried.
        scheduleFlush();
      } catch (e) {
        // Surface in console + telemetry, but don't break the whole SDK —
        // we still want console/network/errors capture even if rrweb fails.
        // eslint-disable-next-line no-console
        console.error("[replay-sdk] rrweb.record failed:", e);
        // No snapshot will ever arrive, so release the flush hold — otherwise the
        // session (console/network/errors, no replay) would buffer forever and
        // never ship. It ships as a no-replay session; the backend keeps it only
        // if it carries analytics/signal, otherwise reaps it.
        rrwebFailed = true;
      }
      if (stop) {
        const stopRec = stop;
        captures.rrweb = {
          stop: () => {
            if (heartbeat) clearInterval(heartbeat);
            stopRec();
          },
        };
      } else if (heartbeat) {
        clearInterval(heartbeat);
      }
      installedPrivacy = nextPrivacy;
    }

    // Remember the URL-redaction policy so other captures (network
    // mostly) can use it to strip query params + credit-card text.
    urlRedactionPolicy = {
      strip: !!effective.stripQueryParams,
      allowed: effective.allowedQueryParams ?? [],
      blockCC: !!effective.blockCreditCardText,
    };
    syncCapture("console", effective.console, () =>
      createConsoleCapture((d: ConsoleEventData) =>
        emit({ ts: Date.now(), type: "console", data: d }),
      ),
    );
    syncCapture("network", effective.network, () =>
      createNetworkCapture(
        {
          ...config,
          captureHeaders: effective.captureHeaders,
          redactUrls: effective.redactUrls ?? config.redactUrls,
        },
        (d: NetworkEventData) =>
          emit({ ts: d.startedAt, type: "network", data: d }),
        () => urlRedactionPolicy,
      ),
    );
    syncCapture("errors", effective.errors, () =>
      createErrorCapture((d: ErrorEventData) => {
        // If sampling-out dropped this session, but the workspace policy
        // says "always record errors", flip sampled back on the moment an
        // error fires. The current event + any in the buffer will ship
        // on the next flush.
        if (!blocked && !sampled && sampleOverrides?.alwaysRecordErrors) {
          sampled = true;
        }
        emit({ ts: Date.now(), type: "error", data: d });
      }),
    );
    syncCapture("navigation", true, () =>
      createNavigationCapture((d: NavigationEventData) => {
        markMeaningful(); // a real route change revives an idle session
        emit({ ts: Date.now(), type: "navigation", data: d });
      }),
    );
    syncCapture("viewport", true, () =>
      createViewportCapture(() => {
        emitViewport();
        scheduleFlush();
      }),
    );
    // Rage + dead-click detector emits as custom events; the persistence
    // worker counts these into session.rageCount / deadCount.
    syncCapture("rageDead", true, () =>
      createRageDeadClickCapture((d) =>
        emit({ ts: Date.now(), type: "custom", data: d }),
      ),
    );
    // Performance signals — LCP/CLS/FID via PerformanceObserver,
    // long tasks for jank, and periodic heap-size snapshots. Each
    // metric is wrapped as a `kind: "perf"` rrweb-style event; the
    // persistence service treats the `type: "performance"` channel
    // exactly like custom rage-click events.
    syncCapture("performance", effective.performance !== false, () =>
      createPerformanceCapture(
        (d) => emit({ ts: d.ts ?? Date.now(), type: "performance", data: d }),
        {
          // Give the resource-timing observer our own ingest origin (same options
          // the phase-1 install passes) so its isServiceURL check can always skip
          // /v1/sdk/config etc. even if the perf slot is re-created via this path.
          apiHost: config.apiHost,
          captureResourceTimings: config.captureResourceTimings,
          resourceMinDurationMs: config.resourceMinDurationMs,
        },
      ),
    );
  };

  // The visibility-gated start. Following the reference tracker, NOTHING is
  // emitted, installed, or flushed until the tab is first VISIBLE — a tab that
  // loads hidden (background-opened, prerendered) records nothing until the user
  // actually looks at it, and one closed while still hidden never creates a
  // session. Runs at most once (the `started` guard).
  const startCapture = () => {
    if (started) return;
    started = true;
    // A FRESH session anchors its clock to NOW (first-visible) so the timeline
    // begins when the page is seen, not when a hidden tab constructed the
    // runtime. A resumed session keeps its original startedAt (offset continuity).
    if (!session.resumed) session.beginFreshClock();

    // A fresh session opens with session_start (sets the entry page). A RESUMED
    // session — a reload or same-tab back/forward within the inactivity window —
    // instead records the reload as a page navigation, so the original entry page
    // is preserved and the reload shows on the timeline as a page transition
    // rather than a second "session start".
    if (session.resumed) {
      emit({
        ts: Date.now(),
        type: "navigation",
        data: {
          to: window.location.href,
          trigger: "load",
        } satisfies NavigationEventData,
      });
    } else {
      emitSessionStart();
    }
    emitViewport();

    // We install captures in *two phases* to avoid losing the rrweb
    // FullSnapshot. Phase 1 starts everything EXCEPT rrweb. Phase 2 runs
    // after remote config arrives (or 300ms timeout, whichever's first) and
    // starts rrweb with the final privacy/canvas options.
    //
    // Why split? rrweb only fires FullSnapshot on initial `record()`. If we
    // started it in phase 1 with stale config, then stopped+restarted in
    // phase 2 because `recordCanvas` flipped, the new instance does NOT
    // re-emit a FullSnapshot (rrweb 2.0-alpha quirk). The Replayer then
    // has incremental snapshots with nothing to apply them to → blank
    // screen with "no replay frames" in the dashboard.
    installCaptures({
      console: config.captureConsole !== false,
      network: config.captureNetwork !== false,
      errors: config.captureErrors !== false,
      // Default-on in phase 1 so we don't miss the LCP window while remote
      // config is in flight. If remote arrives and says off, the next
      // installCaptures call disables it.
      performance: true,
      captureHeaders: config.captureHeaders ?? false,
      maskAllInputs: config.maskAllInputs ?? true,
      // Phase 1 deliberately skips rrweb — see installCaptures internals.
      skipRrweb: true,
    });

    // Promise race: whichever fires first wins. We never wait > 300ms for
    // remote config — a slow /v1/sdk/config request shouldn't delay
    // recording.
    const remotePromise = fetchRemoteConfig(config);
    const timeoutPromise = new Promise<null>((r) =>
      setTimeout(() => r(null), 300),
    );
    void Promise.race([remotePromise, timeoutPromise]).then(async (remote) => {
      // If the race was won by the timeout, await the actual remote in the
      // background so it can still adjust mid-session — but start recording
      // NOW using local config as the source of truth.
      if (remote === null) {
        installCaptures({
          console: config.captureConsole !== false,
          network: config.captureNetwork !== false,
          errors: config.captureErrors !== false,
          performance: true,
          captureHeaders: config.captureHeaders ?? false,
          maskAllInputs: config.maskAllInputs ?? true,
        });
        const lateRemote = await remotePromise.catch(() => null);
        if (lateRemote) applyRemoteConfig(lateRemote);
        return;
      }
      applyRemoteConfig(remote);
    });

    // Start the periodic flush now (not while hidden). Its first tick and any
    // scheduleFlush() are still held by the snapshot gate until the base exists.
    session.startAutoFlush(flush);
  };

  function applyRemoteConfig(
    remote: NonNullable<Awaited<ReturnType<typeof fetchRemoteConfig>>>,
  ) {
    // ---- Hard cap gate ----------------------------------------------
    // The workspace has spent its plan's session allowance. Record NOTHING and
    // ship NOTHING — this beats every "always record" override below (a maxed
    // plan doesn't record errors or identified users either). The server also
    // rejects any batch that slips through, but honouring the flag here is what
    // saves the upload: the device does no work at all.
    if (remote.shouldNotRecord) {
      blocked = true;
      sampled = false;
      return;
    }

    // Host-configured URL/route EXCLUSION — the "don't record this screen" opt-out
    // for web. A deny-list of path globs (e.g. "/settings/billing", "/admin/**")
    // beats every "always record" override and the sample roll; like
    // shouldNotRecord, an excluded URL does NO capture work and ships nothing.
    // NOTE (SPA follow-up): this is evaluated at init/config time. A single-page
    // app that CLIENT-navigates into an excluded route mid-session is not yet
    // re-gated — that needs a navigation hook that pauses/resumes capture.
    if (
      (config.excludeUrls ?? []).some((g) =>
        matchesGlob(g, window.location.href),
      )
    ) {
      blocked = true;
      sampled = false;
      return;
    }

    // ---- Sampling decision ------------------------------------------
    // Start by rolling the sample rate. The "always record" overrides
    // below can flip the decision back on for this particular session.
    const url = window.location.href;
    const urlForced = (remote.sampling.alwaysRecordOnUrls ?? []).some((g) =>
      matchesGlob(g, url),
    );
    const triggerAllowsRecord =
      remote.capture.trigger !== "identified" || !!config.distinctId;
    const sampledIn = urlForced || Math.random() <= remote.sampling.rate;
    sampled = sampledIn && triggerAllowsRecord;
    if (!sampled) {
      // Still install the rest of the captures so identify() can flip
      // sampling on later. We just won't ship the batches.
    }

    // ---- Capture toggles --------------------------------------------
    // maskSelectors/blockSelectors arrive as arrays of CSS selectors;
    // rrweb takes a single comma-joined string for each.
    const maskTextSelector =
      (remote.privacy.maskSelectors ?? []).join(", ") || undefined;
    const blockSelector =
      (remote.privacy.blockSelectors ?? []).join(", ") || undefined;

    // Build rrweb's maskInputOptions from the server-derived input-type
    // list. `password` is always included even when the customer disables
    // maskAllInputs — that's the security floor.
    const maskInputOptions: Record<string, boolean> = {};
    for (const t of remote.privacy.maskInputTypes ?? ["password"]) {
      maskInputOptions[t] = true;
    }

    installCaptures({
      console: remote.capture.console,
      network: remote.capture.network,
      errors: remote.capture.errors,
      performance: remote.capture.performance,
      captureHeaders: remote.capture.networkHeaders ?? remote.capture.headers,
      captureBodies: remote.capture.networkBodies,
      maskAllInputs: remote.privacy.maskAllInputs,
      maskInputOptions,
      maskTextSelector,
      blockSelector,
      recordCanvas: remote.capture.canvas,
      recordCrossOriginIframes: remote.capture.iframe,
      blockCreditCardText: remote.privacy.blockCreditCardText,
      stripQueryParams: remote.privacy.stripQueryParams,
      allowedQueryParams: remote.privacy.allowedQueryParams,
      redactUrls: remote.privacy.redactUrlPatterns.map(
        (p) => new RegExp(p, "i"),
      ),
    });

    // Remember the rules so identify()-triggered re-evaluation can flip
    // sampling on later when alwaysRecordIdentified=true.
    sampleOverrides = {
      alwaysRecordErrors: remote.sampling.alwaysRecordErrors,
      alwaysRecordIdentified: remote.sampling.alwaysRecordIdentified,
    };
    minDurationMs = remote.capture.minDurationMs ?? 0;
  } // end applyRemoteConfig

  // Tiny glob matcher — supports `**` (matches anything) and `*` (matches
  // anything not /). Enough for `/checkout/**` style patterns.
  function matchesGlob(glob: string, url: string): boolean {
    try {
      const path = new URL(url).pathname;
      const rx =
        "^" +
        glob
          .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
          .replace(/\*\*/g, "::DOUBLE::")
          .replace(/\*/g, "[^/]*")
          .replace(/::DOUBLE::/g, ".*") +
        "$";
      return new RegExp(rx).test(path);
    } catch {
      return false;
    }
  }

  // Deliver the tail reliably as the page goes away. visibilitychange:hidden is
  // the signal that fires FIRST on a tab close (and the only one guaranteed when
  // a mobile tab is backgrounded / on bfcache), so the closing frames are
  // usually shipped while the page is still alive; pagehide + beforeunload mop
  // up anything left. Each call drains the buffer, so the later ones are no-ops
  // when an earlier one already sent — no duplicate batches.
  const finalFlush = (reason: SessionEndEventData["reason"]) => {
    // Never create a server-side session on the way out for a tab that never
    // visibly started, or whose snapshot never arrived (and rrweb didn't fail) —
    // same gate flush() uses. A load-hidden-then-closed tab and a pre-snapshot
    // bounce both ship nothing.
    if (!started || (!snapshotReady && !rrwebFailed)) return;
    // Discard a bounce session that never reached minDuration — don't ship its
    // held buffer on the way out.
    if (minDurationMs > 0 && Date.now() - session.startedAt < minDurationMs) {
      return;
    }
    emitSessionEnd(reason);
    session.flushFinal(sendBatch.sendFinal);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") finalFlush("visibility_hidden");
  });
  window.addEventListener("pagehide", () => finalFlush("unload"));
  window.addEventListener("beforeunload", () => finalFlush("unload"));

  // Keep the session's last-activity marker fresh on genuine USER interaction —
  // deliberately NOT the SDK's own heartbeat/perf events, so an idle tab that's
  // only emitting keepalive snapshots still ages out of the resume window. These
  // are passive + capture so they never delay the page's own handlers, and the
  // write behind touch() is throttled, so this is effectively free on the hot
  // pointer/scroll path.
  const onUserActivity = () => markMeaningful();
  const activityEvents = [
    "pointerdown",
    "keydown",
    "scroll",
    "touchstart",
  ] as const;
  for (const evt of activityEvents) {
    window.addEventListener(evt, onUserActivity, {
      passive: true,
      capture: true,
    });
  }

  // Live inactivity timeout: end the session after `inactivityMs` with no
  // meaningful activity (GA's session model + the reference tracker's own
  // "stop session after inactivity timeout" TODO). Poll at a fraction of the
  // window (≤30s) — cheap, and precise enough since the window is minutes.
  // sessionInactivityMs = 0 (or huge) effectively disables it.
  if (session.inactivityMs > 0) {
    const checkEvery = Math.min(
      30_000,
      Math.max(1_000, Math.floor(session.inactivityMs / 4)),
    );
    idleTimer = window.setInterval(() => {
      if (!idle && started && Date.now() - lastMeaningfulAt >= session.inactivityMs) {
        endForIdle();
      }
    }, checkEvery);
  }

  // Kick off the visibility-gated start. If the tab is already visible, start
  // synchronously — unchanged behavior for the overwhelmingly common case. If it
  // loaded hidden (background-opened tab / prerender, which report "hidden"),
  // defer the entire start to the first time it becomes visible, then unregister,
  // so a hidden tab is never recorded and — if it's closed while still hidden —
  // no session is ever created.
  if (document.visibilityState !== "hidden") {
    startCapture();
  } else {
    const onFirstVisible = () => {
      if (document.visibilityState !== "hidden") {
        document.removeEventListener("visibilitychange", onFirstVisible);
        startCapture();
      }
    };
    document.addEventListener("visibilitychange", onFirstVisible);
  }

  const identify: ReplayController["identify"] = (idOrPayload, props) => {
    const payload: IdentifyPayload =
      typeof idOrPayload === "string"
        ? { distinctId: idOrPayload, ...(props ?? {}) }
        : { ...idOrPayload };
    sendBatch.setIdentify(payload);
    markMeaningful(); // identify is engagement — revives an idle session
    // Emit a DISCRETE, timestamped identify marker into the stream (parity with
    // the reference tracker's UserID message) so the exact moment of anon→known
    // is a first-class timeline event, not just an attribute on later batches.
    if (payload.distinctId || payload.email) {
      emit({
        ts: Date.now(),
        type: "custom",
        data: {
          kind: "identify",
          distinctId: payload.distinctId ?? payload.email,
          ...(payload.email ? { email: payload.email } : {}),
        },
      });
    }
    // Workspace policy: "always record identified users" flips a
    // previously-dropped session back into recording the moment the
    // customer calls identify(). Saves an "I had this user but no
    // session" support ticket.
    if (
      !blocked &&
      !sampled &&
      sampleOverrides?.alwaysRecordIdentified &&
      payload.distinctId
    ) {
      sampled = true;
    }
    void flush();
  };

  // Host-supplied anonymous/device id — override the SDK's generated fingerprint
  // so the same person is one anonymous user across the customer's systems.
  // Persisted so it survives reloads.
  const setAnonymousId: ReplayController["setAnonymousId"] = (id) => {
    if (!id || typeof id !== "string") return;
    const trimmed = id.trim().slice(0, 200);
    if (!trimmed) return;
    setStoredFingerprint(trimmed);
    sendBatch.setFingerprint(trimmed);
  };

  // Free-form session trait, independent of user identity. Captured into the
  // stream as a custom event so it's queryable + timeline-visible.
  const setMetadata: ReplayController["setMetadata"] = (key, value) => {
    if (!key || typeof key !== "string") return;
    emit({
      ts: Date.now(),
      type: "custom",
      data: {
        kind: "session_metadata",
        key: key.trim().slice(0, 80),
        value: typeof value === "string" ? value.slice(0, 500) : String(value),
      },
    });
    void flush();
  };

  // Custom event tracking. Rides along in the batch as a `custom`
  // event with `kind: "track"` so the backend can pluck names into a
  // denormalised `Session.eventNames[]` without breaking older
  // consumers that already match `kind: "rage_click" | "dead_click"`.
  const track: ReplayController["track"] = (name, properties) => {
    if (!name || typeof name !== "string") return;
    const safeName = name.trim().slice(0, 80);
    if (!safeName) return;
    markMeaningful(); // a tracked event is engagement — revives an idle session
    emit({
      ts: Date.now(),
      type: "custom",
      data: {
        kind: "track",
        name: safeName,
        // Cap properties to keep batches small. Stringify defensively
        // so we don't ship Functions / DOM nodes.
        properties: properties ? safeProps(properties) : undefined,
      },
    });
  };

  // Report a developer-caught exception as the same `error` event the automatic
  // window handlers emit, marked handled (so the dashboard classifies it as an
  // "exception" vs an uncaught "crash"). Mirrors track()'s emit path.
  const captureException: ReplayController["captureException"] = (
    error,
    opts,
  ) => {
    // Same policy as the automatic error path: if sampling-out dropped this
    // session but the workspace says "always record errors", a reported
    // exception flips recording back on — otherwise the handled error the
    // feature exists to surface would be discarded on the next flush.
    if (!blocked && !sampled && sampleOverrides?.alwaysRecordErrors) {
      sampled = true;
    }
    markMeaningful(); // an explicitly-reported exception is engagement
    emit({
      ts: Date.now(),
      type: "error",
      data: errorDataFromValue(error, opts?.handled ?? true, opts?.metadata),
    });
  };

  return {
    // Getter — rotate() (inactivity return) changes the id mid-flight, so a host
    // reading `replay.sessionId` must see the CURRENT session, not the init one.
    get sessionId() {
      return session.sessionId;
    },
    flush,
    identify,
    track,
    captureException,
    setAnonymousId,
    setMetadata,
    // Deep link to THIS session in the dashboard — the private, authed
    // /recordings/<id> route (NOT a public share link). Read the live session id
    // (it rotates on inactivity), and derive the app host from apiHost.
    urlForCurrentSession: () =>
      session.sessionId
        ? `${dashboardHostFromApiHost(config.apiHost)}/recordings/${session.sessionId}`
        : null,
    stop: async () => {
      if (idleTimer !== undefined) window.clearInterval(idleTimer);
      for (const k of Object.keys(captures)) {
        const slot = captures[k];
        if (slot) {
          try {
            slot.stop();
          } catch {
            /* ignore */
          }
          captures[k] = null;
        }
      }
      for (const evt of activityEvents) {
        window.removeEventListener(evt, onUserActivity, {
          capture: true,
        } as EventListenerOptions);
      }
      session.stopAutoFlush();
      emitSessionEnd("manual");
      await flush();
    },
  };
}

/**
 * Coerce arbitrary properties into a JSON-safe shape before shipping
 * with a track() event. We keep:
 *   - primitives (string/number/boolean/null)
 *   - arrays of primitives
 *   - plain objects one level deep
 * Anything else (DOM nodes, functions, circular structures) is
 * dropped. Caps at 20 keys + 200 chars per value so a single noisy
 * call can't blow the batch size budget.
 */
function safeProps(p: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let i = 0;
  for (const k of Object.keys(p)) {
    if (i >= 20) break;
    i++;
    const v = p[k];
    if (v === null || typeof v === "boolean" || typeof v === "number") {
      out[k] = v;
    } else if (typeof v === "string") {
      out[k] = v.slice(0, 200);
    } else if (Array.isArray(v)) {
      out[k] = v.slice(0, 20).map((x) =>
        typeof x === "string" ? x.slice(0, 200) : x,
      );
    } else if (v && typeof v === "object") {
      // Flatten one level — { from: "free", to: "pro" } is the
      // typical track() payload. Anything deeper gets toString'd.
      try {
        out[k] = JSON.parse(JSON.stringify(v));
      } catch {
        out[k] = String(v).slice(0, 200);
      }
    }
  }
  return out;
}
